﻿/**
 * StateManagement Class
 * Entry point for all SDK state. Builds the state sub-modules and exposes each
 * one through a read-only getter; the sub-modules do the actual work.
 * This class is NOT exposed to the consuming application.
 */

import type { InitConfig, CommandDispatcher } from '@anddone/ajs-util';
import { createLogger } from '@anddone/ajs-logger';
import type { Events } from './Events';
import type { FragmentRegistry } from './fragment-registry';
import {
  CommandDispatcherRef,
  ConfigState,
  FeesAndTotalsManager,
  MetadataDisplayManager,
  PaymentAttemptManager,
  PaymentAuthorizationManager,
  PaymentMethodManager,
  SessionState,
  SettingsState,
} from './state-management-module';

const logger = createLogger('SDK', 'StateManagement');

export class StateManagement {
  // TC39 private fields — inaccessible from outside this class at runtime.
  readonly #config: ConfigState;
  readonly #session: SessionState;
  readonly #settings: SettingsState;
  readonly #attempts: PaymentAttemptManager;
  readonly #fees: FeesAndTotalsManager;
  readonly #metadataDisplay: MetadataDisplayManager;
  readonly #paymentAuthorization: PaymentAuthorizationManager;
  readonly #paymentMethod: PaymentMethodManager;
  readonly #dispatcher = new CommandDispatcherRef();

  /** Validated consumer init config and the URLs/origin derived from it. */
  public get config(): ConfigState {
    return this.#config;
  }
  /** Session identity and command token. */
  public get session(): SessionState {
    return this.#session;
  }
  /** Merchant and payment intent settings from the settings endpoint. */
  public get settings(): SettingsState {
    return this.#settings;
  }
  /** Payment attempts and in-progress submission tracking. */
  public get attempts(): PaymentAttemptManager {
    return this.#attempts;
  }
  /** Active payment method. */
  public get paymentMethod(): PaymentMethodManager {
    return this.#paymentMethod;
  }
  /** Computed fees and totals. */
  public get fees(): FeesAndTotalsManager {
    return this.#fees;
  }
  /** Payment description and reference data from settings. */
  public get metadataDisplay(): MetadataDisplayManager {
    return this.#metadataDisplay;
  }
  /** Selected payment authorization message. */
  public get paymentAuthorization(): PaymentAuthorizationManager {
    return this.#paymentAuthorization;
  }

  constructor(configFromConsumer: InitConfig, fragmentRegistry: FragmentRegistry, events: Events) {
    this.#config = new ConfigState(configFromConsumer);
    this.#session = new SessionState();
    this.#settings = new SettingsState();
    this.#attempts = new PaymentAttemptManager();
    this.#fees = new FeesAndTotalsManager(this.#settings, this.#dispatcher, events);
    this.#metadataDisplay = new MetadataDisplayManager(this.#settings, this.#dispatcher, events);
    this.#paymentAuthorization = new PaymentAuthorizationManager(
      this.#settings,
      this.#dispatcher,
      events,
    );
    this.#paymentMethod = new PaymentMethodManager(
      this.#settings,
      this.#dispatcher,
      events,
      fragmentRegistry,
      this.#fees,
      this.#paymentAuthorization,
    );

    logger.info('init', `State management module initialized`);
  }

  /**
   * Bind the dispatcher that state sub-modules use to send commands.
   *
   * @param dispatcher - The SDK's command dispatcher (FragmentCommandManager)
   * @returns void
   */
  public bindCommandDispatcher(dispatcher: CommandDispatcher): void {
    this.#dispatcher.bind(dispatcher);
  }

  /**
   * Publish the settings-derived state once fragments are mounted: resolve the
   * initial payment method, then publish fees and payment authorization.
   *
   * @returns void
   */
  public publishInitialState(): void {
    const overridden = this.#paymentMethod.resolveInitial();

    // An override goes through update(), which already republishes.
    if (!overridden) {
      this.#paymentMethod.publishDerivedState();
    }

    // Metadata display is payment-method-independent — publish once after settings are loaded.
    this.#metadataDisplay.publish();
  }
}

// Freezing the prototype stops external code from redefining the getters to
// shadow the underlying sub-modules, and prevents the methods from being replaced.
Object.freeze(StateManagement.prototype);
